import styled from "styled-components/macro";
import DownloadIcon from "@mui/icons-material/GetApp";
import { CSVLink } from "react-csv";
import { IconButton, Tooltip } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useReportConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import MenuItem from "@mui/material/MenuItem";
import { Menu } from "@mui/material";
import { formatHoursMinutes } from "utils/timeUtils";
import { useState } from "react";

export type CSVRow = {
  ge?: number;
  lt?: number;
  category?: string;
  rangeStart?: number;
  rangeEnd?: number;
  time?: number;
  title?: string;
  type?: string;
  shiftName?: string;
  sku?: string;
  skuDescription?: string;
  skuTargetRate?: number;
  skuRateDenomMs?: number;
  operator?: string;
  machemetryIds?: string;
  processNames?: string;
  collectedTime?: number;
  operationTime?: number;
  opportunityTime?: number;
  productionTargetOutput?: number;
  productionTime?: number;
  outputLossSum?: number;
  producing?: number;
  planned?: number;
  waiting?: number;
  unplanned?: number;
  unknown?: number;
  notScheduled?: number;
  uncollected?: number;
  oeePercent?: number;
  oeePercentTopDown?: number;
  oeePercentBottomUp?: number;
  teepPercent?: number;
  teepPercentBottomUp?: number;
  availabilityPercent?: number;
  uptimePercent?: number;
  performancePercent?: number;
  performancePercentBottomUp?: number;
  producingEfficiencyPercent?: number;
  producingEfficiencyPercentBottomUp?: number;
  qualityYieldPercent?: number;
  netOutput?: number;
  grossOutput?: number;
  rejectOutput?: number;
  manualRejectOutput?: number;
  uslePercent?: number;
  operationalAvailabilityPercent?: number;
  efficiencyPercent?: number;
  pureEquipmentUptimePercent?: number;
  uptimePerformancePercent?: number;
  opportunityPercent?: number;
  taggingPercent?: number;
  totalPotentialOutput?: number;
  taggingByEventPercent?: number;
  taggedNumberOfEvents?: number;
  untaggedNumberOfEvents?: number;
  netTicks?: number;
  grossTicks?: number;
  rejectTicks?: number;
  ticksPort1?: number;
  ticksPort2?: number;
  ticksPort3?: number;
  ticksPort4?: number;
  meanTimeToRecovery?: number;
  meanTimeBetweenFailures?: number;
  avgCycleTime?: number;
};

export default function CSVDownloadButton({
  fileName,
  rows,
}: {
  fileName: string;
  rows: CSVRow[];
}) {
  const { translate } = useFlightTranslation();
  const parsed = parseToCSVFriendly(rows);
  const headers = parsed[0];

  const reportConfig = useReportConfig();
  const csvDownloadEnableReadableDates =
    reportConfig?.eosReportGeneralConfig?.csvDownloadEnableReadableDates ??
    false;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <CSVLinkStyles>
        <Tooltip title={translate("Download_CSV")}>
          <IconButton
            id="csv-download-button"
            onClick={() =>
              csvDownloadEnableReadableDates
                ? setAnchorEl(document.getElementById("csv-download-button"))
                : null
            }
          >
            {csvDownloadEnableReadableDates ? (
              <DownloadIcon />
            ) : (
              <CSVLink filename={fileName} data={parsed}>
                <DownloadIcon />
              </CSVLink>
            )}
          </IconButton>
        </Tooltip>
      </CSVLinkStyles>
      {csvDownloadEnableReadableDates && (
        // a menu with two options: "Download CSV (milliseconds)" and "Download CSV (readable dates)"
        <Menu
          id="csv-download-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          disableScrollLock={true}
        >
          <CSVLink filename={fileName} data={parsed}>
            <MenuItem>
              Download CSV {"("}milliseconds{")"}
            </MenuItem>
          </CSVLink>
          <CSVLink
            filename={fileName}
            data={(() => {
              const parsed_readable = parseReadableCsv(parsed, headers);
              return parsed_readable;
            })()}
          >
            <MenuItem>
              Download CSV {"("}human-readable{")"}
            </MenuItem>
          </CSVLink>
        </Menu>
      )}
    </>
  );
}

function parseReadableCsv(parsed: string[][], headers: string[]) {
  return parsed.map(row => {
    // if the row is the header row, return it as is
    if (row[0] === "type") {
      return row;
    }

    // otherwise, for each item...
    return row.map((item, index) => {
      const associatedHeader = headers[index];

      const isNumber = !isNaN(Number(item));
      if (!isNumber) {
        return item;
      }
      // if the item is a timestamp (ge, lt), convert it to human-readable date
      const isTimestamp = ["ge", "lt"].includes(associatedHeader);
      if (isTimestamp) {
        return new Date(Number(item)).toLocaleString();
      }

      // if the item is a percent (<4 digits rounded to 0 decimal places), leave it as-is
      const isPercent = PERCENT_COLUMNS.includes(associatedHeader);

      const isCount = COUNT_COLUMNS.includes(associatedHeader);

      if (isPercent || isCount) {
        return item;
      }

      // if the item is a duration, convert it to hours:minutes:seconds
      const isDuration = DURATION_COLUMNS.includes(associatedHeader);
      if (isDuration) {
        return formatHoursMinutes(Number(item));
      }
      return item;
    });
  });
}

const PERCENT_COLUMNS = [
  "oeePercent",
  "oeePercentTopDown",
  "oeePercentBottomUp",
  "teepPercent",
  "teepPercentBottomUp",
  "availabilityPercent",
  "uptimePercent",
  "performancePercent",
  "performancePercentBottomUp",
  "producingEfficiencyPercent",
  "producingEfficiencyPercentBottomUp",
  "qualityYieldPercent",
  "pureEquipmentUptimePercent",
  "uptimePerformancePercent",
  "opportunityPercent",
  "taggingPercent",
  "taggingByEventPercent",
  "untaggedPercent",
  "notScheduledPercent",
];
const COUNT_COLUMNS = [
  "netOutput",
  "grossOutput",
  "rejectOutput",
  "manualRejectOutput",
  "productionTargetOutput",
  "outputLossSum",
  "netTicks",
  "grossTicks",
  "rejectTicks",
  "ticksPort1",
  "ticksPort2",
  "ticksPort3",
  "ticksPort4",
  "taggedNumberOfEvents",
  "untaggedNumberOfEvents",
];
const DURATION_COLUMNS = [
  "collectedTime",
  "operationTime",
  "opportunityTime",
  "productionTime",
  "outputLossSum",
  "meanTimeToRecovery",
  "meanTimeBetweenFailures",
  "avgCycleTime",
];
export function parseToCSVFriendly(data: CSVRow[]): string[][] {
  const headers = Object.keys(data[0]) as (keyof CSVRow)[];
  const csvData: string[][] = [headers];

  for (const row of data) {
    const rowData: string[] = [];
    for (const header of headers) {
      rowData.push(String(row[header]));
    }
    csvData.push(rowData);
  }

  return csvData;
}

const CSVLinkStyles = styled.div`
  display: flex;
  border-radius: 999px;
  box-sizing: border-box;
  .MuiIconButton-root {
    width: 48px;
    height: 48px;
  }
  a {
    width: 48px;
    height: 48px;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .MuiSvgIcon-root {
    color: white;
  }
  @media print {
    display: none;
  }
`;

export function parseBreakdownToCSVRow(
  breakdown: Record<string, any> | null,
  title: string
): CSVRow {
  const row: Partial<CSVRow> & { type: string } = { type: title };
  for (const [key, value] of Object.entries(breakdown ?? {})) {
    if (
      typeof value === "object" ||
      ["__typename", "percentWidth"].includes(key)
    ) {
      continue;
    }
    row[key] = value;
  }
  return row as CSVRow;
}
